import { useState } from "react";
import { Alert, Button, Collapse, Drawer, Input, Segmented, Space, Tag, Typography } from "antd";

import {
  useDraftCollectionsAssistant,
  useRecordCollectionsAction,
  useSendCollectionsCustomMessage,
  type CollectionsAssistantDraft,
  type CollectionsAssistantKind,
  type CollectionsAssistantResponse,
  type CollectionsPriority,
} from "../hooks/useCollections";

const KIND_OPTIONS: { value: CollectionsAssistantKind; label: string }[] = [
  { value: "email", label: "Mail" },
  { value: "sms", label: "SMS" },
  { value: "call_script", label: "Skrypt rozmowy" },
  { value: "summary", label: "Podsumowanie sprawy" },
];

const PRIORITY: Record<CollectionsPriority, { color: string; label: string }> = {
  high: { color: "red", label: "priorytet wysoki" },
  medium: { color: "gold", label: "priorytet średni" },
  low: { color: "default", label: "priorytet niski" },
};

interface AssistantDrawerProps {
  open: boolean;
  caseId: string;
  clientPhone: string | null;
  onClose: () => void;
}

const scriptAsText = (draft: Extract<CollectionsAssistantDraft, { kind: "call_script" }>["draft"]) =>
  [
    `Otwarcie: ${draft.opening}`,
    "",
    "Pytania:",
    ...draft.questions.map((question, index) => `${index + 1}. ${question}`),
    "",
    "Obiekcje:",
    ...draft.objections.map((entry) => `- ${entry.objection} → ${entry.answer}`),
    "",
    `Propozycja: ${draft.proposedSolution}`,
    `Zamknięcie: ${draft.closing}`,
  ].join("\n");

const summaryAsText = (draft: Extract<CollectionsAssistantDraft, { kind: "summary" }>["draft"]) =>
  [
    draft.summary,
    "",
    `Rekomendacja: ${draft.recommendation}`,
    ...(draft.risks.length ? ["", "Ryzyka:", ...draft.risks.map((risk) => `- ${risk}`)] : []),
  ].join("\n");

/**
 * Stage 1 of the collections agent (TRE-2171): the assistant drafts, Anita
 * reads, edits and sends. Every send lands on the timeline as a custom
 * message; a script or a summary can be kept as a note.
 */
const AssistantDrawer = ({ open, caseId, clientPhone, onClose }: AssistantDrawerProps) => {
  const [kind, setKind] = useState<CollectionsAssistantKind>("email");
  const [instructions, setInstructions] = useState("");
  const [response, setResponse] = useState<CollectionsAssistantResponse | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const draftMutation = useDraftCollectionsAssistant();
  const sendMutation = useSendCollectionsCustomMessage();
  const noteMutation = useRecordCollectionsAction();

  const generate = () =>
    draftMutation.mutate(
      { caseId, kind, instructions },
      {
        onSuccess: (result) => {
          setResponse(result);
          const { draft } = result.result;
          if (result.result.kind === "email") {
            setSubject(result.result.draft.subject);
            setBody(result.result.draft.body);
          } else if (result.result.kind === "sms") {
            setSubject("");
            setBody(result.result.draft.text);
          } else if (result.result.kind === "call_script") {
            setSubject("");
            setBody(scriptAsText(result.result.draft));
          } else {
            setSubject("");
            setBody(summaryAsText(draft as Extract<CollectionsAssistantDraft, { kind: "summary" }>["draft"]));
          }
        },
      },
    );

  const priority = response?.result.draft.priority;
  const isMessage = kind === "email" || kind === "sms";

  return (
    <Drawer open={open} onClose={onClose} width={640} title="Asystent windykacji" destroyOnHidden>
      <Space direction="vertical" size="middle" className="w-full">
        <Segmented
          block
          options={KIND_OPTIONS}
          value={kind}
          onChange={(value) => {
            setKind(value as CollectionsAssistantKind);
            setResponse(null);
            setBody("");
            setSubject("");
          }}
        />
        <Input.TextArea
          rows={2}
          placeholder="Wskazówki dla asystenta (opcjonalnie): np. „krócej”, „zaproponuj przesunięcie o 2 tygodnie”, „nawiąż do rozmowy z wtorku”"
          value={instructions}
          onChange={(event) => setInstructions(event.target.value)}
          maxLength={1000}
        />
        <Button type="primary" onClick={generate} loading={draftMutation.isLoading}>
          {response ? "Przygotuj jeszcze raz" : "Przygotuj"}
        </Button>

        {response ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {priority ? <Tag color={PRIORITY[priority].color}>{PRIORITY[priority].label}</Tag> : null}
              <Typography.Text type="secondary">{response.result.draft.priorityReason}</Typography.Text>
            </div>
            {"rationale" in response.result.draft ? (
              <Alert type="info" showIcon message="Dlaczego tak" description={response.result.draft.rationale} />
            ) : null}

            {kind === "email" ? (
              <Input
                addonBefore="Temat"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                maxLength={150}
              />
            ) : null}
            <Input.TextArea
              rows={kind === "sms" ? 4 : 14}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={kind === "sms" ? 320 : 5000}
              showCount
            />

            <div className="flex flex-wrap gap-2">
              {isMessage ? (
                <Button
                  type="primary"
                  loading={sendMutation.isLoading}
                  disabled={!body.trim() || (kind === "sms" && !clientPhone)}
                  onClick={() =>
                    sendMutation.mutate(
                      {
                        caseId,
                        channel: kind === "sms" ? "sms" : "email",
                        subject: subject || undefined,
                        body: body.trim(),
                      },
                      { onSuccess: onClose },
                    )
                  }
                >
                  {kind === "sms" ? "Wyślij SMS" : "Wyślij mail"}
                </Button>
              ) : (
                <Button
                  type="primary"
                  loading={noteMutation.isLoading}
                  disabled={!body.trim()}
                  onClick={() =>
                    noteMutation.mutate(
                      {
                        caseId,
                        action: {
                          type: "note",
                          note: `${kind === "summary" ? "Podsumowanie" : "Skrypt rozmowy"} (asystent):\n${body.trim()}`,
                        },
                      },
                      { onSuccess: onClose },
                    )
                  }
                >
                  Zapisz jako notatkę
                </Button>
              )}
              {kind === "sms" && !clientPhone ? (
                <Typography.Text type="danger">Klientka nie ma numeru telefonu.</Typography.Text>
              ) : null}
            </div>

            <Collapse
              size="small"
              items={[
                {
                  key: "brief",
                  label: `Co widział asystent (${response.model}, ${response.usage.inputTokens + response.usage.outputTokens} tokenów${response.usage.costUsd !== null ? `, ~$${response.usage.costUsd.toFixed(4)}` : ""})`,
                  children: <pre className="whitespace-pre-wrap text-xs text-gray-600">{response.brief}</pre>,
                },
              ]}
            />
          </>
        ) : (
          <Typography.Text type="secondary">
            Asystent czyta kartę sprawy, historię kontaktu i aktywność klientki w programie. Przygotowuje tekst, który
            Ty czytasz, poprawiasz i wysyłasz. Nic nie wychodzi bez Twojego kliknięcia.
          </Typography.Text>
        )}
      </Space>
    </Drawer>
  );
};

export default AssistantDrawer;
